/**
 * Yandex Metrika (counter 113726758) — single entry point for every event.
 *
 * Privacy: never pass e-mail, phone, Telegram username or payment details to
 * the counter. Only product/order identifiers, names and amounts go through.
 *
 * The counter is initialised in index.html with `ssr: true`, i.e. the tag does
 * NOT send the first hit itself; `trackHit()` sends it once the SPA is mounted
 * and on every route change, so a visit is never double-counted.
 */
export const YM_COUNTER_ID = 113726758

declare global {
  interface Window {
    ym?: (...args: unknown[]) => void
    dataLayer?: Record<string, unknown>[]
  }
}

export function ym(...args: unknown[]): void {
  try {
    window.ym?.(YM_COUNTER_ID, ...args)
  } catch {
    /* analytics must never break the shop */
  }
}

export function reachGoal(goal: string, params?: Record<string, unknown>): void {
  ym('reachGoal', goal, params)
}

// ── Pageview tracking (SPA) ────────────────────────────────────────────────
let lastHitUrl = ''

export function trackHit(url: string = window.location.href): void {
  if (url === lastHitUrl) return
  lastHitUrl = url
  ym('hit', url, {
    title: document.title,
    referrer: document.referrer || undefined,
  })
}

// ── Ad-source (UTM / Yandex Direct / Google Ads) preservation ─────────────
// Metrika attributes the whole session by the first hit's URL. We also keep
// the tags in sessionStorage so the purchase event can be tied to the source
// inside our own admin (never sent as PII).
const UTM_KEY = 'cr_metrika_source'
const SOURCE_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'yclid',
  'gclid',
] as const

export function captureTrafficSource(): void {
  try {
    const q = new URLSearchParams(window.location.search)
    const found: Record<string, string> = {}
    for (const k of SOURCE_KEYS) {
      const v = q.get(k)
      if (v) found[k] = v.slice(0, 200)
    }
    if (!Object.keys(found).length) return
    const prev = trafficSource()
    sessionStorage.setItem(UTM_KEY, JSON.stringify({ ...prev, ...found }))
  } catch {
    /* storage may be unavailable (private mode) */
  }
}

export function trafficSource(): Record<string, string> {
  try {
    return JSON.parse(sessionStorage.getItem(UTM_KEY) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}

// ── Purchase (the key goal) ────────────────────────────────────────────────
const PAID_KEY_PREFIX = 'cr_ym_paid_'

export type PurchasePayload = {
  orderRef: string
  /** Actually paid amount in ₽ (after promo, cashback, acquiring markup). */
  paidAmount: number
  productId: string
  productName: string
  category: string
  quantity?: number
  currency?: string
}

/**
 * Send the e-commerce purchase + `pay_success` goal. MUST only be called after
 * the server (payment webhook / reconciler) confirmed the payment — never on
 * order creation or when the payment page opens.
 *
 * Idempotent per order: a reload, a second visit to /payment-success, or a
 * duplicated webhook cannot count the same purchase twice (localStorage flag).
 */
export function trackPurchase(p: PurchasePayload): boolean {
  try {
    if (localStorage.getItem(PAID_KEY_PREFIX + p.orderRef)) return false
  } catch {
    return false
  }
  const revenue = Math.round(p.paidAmount * 100) / 100
  if (!(revenue > 0)) return false

  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      ecommerce: {
        purchase: {
          actionField: { id: p.orderRef, revenue, currencyCode: p.currency ?? 'RUB' },
          products: [
            {
              id: p.productId,
              name: p.productName,
              category: p.category,
              price: revenue,
              quantity: p.quantity ?? 1,
            },
          ],
        },
      },
    })
    reachGoal('pay_success', { order_id: p.orderRef, revenue, currency: p.currency ?? 'RUB' })
    localStorage.setItem(PAID_KEY_PREFIX + p.orderRef, String(Date.now()))
    return true
  } catch {
    return false
  }
}

/** Human-readable category for the order's service type (no PII). */
export function serviceCategory(serviceType: string | null | undefined): string {
  switch (serviceType) {
    case 'tiktok':
      return 'TikTok Coins'
    case 'stars':
      return 'Telegram Stars'
    case 'steam':
      return 'Steam'
    case 'dg_topup':
      return 'Пополнение игр'
    case 'dg_voucher':
      return 'Подарочные карты'
    default:
      return 'Заказ'
  }
}
